import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Eye, 
  Package,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const ProductsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { company } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const categories = [
    'Lanches',
    'Bebidas',
    'Salgados',
    'Doces',
    'Sucos',
    'Refrigerantes',
    'Água',
    'Snacks',
    'Outros'
  ];

  useEffect(() => {
    if (company?.id) {
      loadProducts();
    }
  }, [company]);

  useEffect(() => {
    // Mostrar mensagem de sucesso se veio da página de cadastro
    if (location.state?.message) {
      toast.success(location.state.message);
      // Limpar state para não mostrar novamente
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await dataHelpers.getProducts(company.id);
      setProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Erro ao carregar produtos');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Tem certeza que deseja excluir o produto "${product.name}"?`)) {
      return;
    }

    try {
      await dataHelpers.deleteProduct(product.id);
      setProducts(prev => prev.filter(p => p.id !== product.id));
      toast.success('Produto excluído com sucesso');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erro ao excluir produto');
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.barcode?.includes(searchTerm);
    
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const stats = {
    total: products.length,
    active: products.filter(p => p.active).length,
    lowStock: products.filter(p => p.stock_quantity <= p.min_stock).length,
    totalValue: products.reduce((sum, p) => sum + (p.price * p.stock_quantity), 0)
  };

  const getStockStatus = (product) => {
    if (product.stock_quantity === 0) {
      return { status: 'out', label: 'Sem estoque', color: 'bg-red-100 text-red-800' };
    } else if (product.stock_quantity <= product.min_stock) {
      return { status: 'low', label: 'Estoque baixo', color: 'bg-yellow-100 text-yellow-800' };
    } else {
      return { status: 'ok', label: 'Em estoque', color: 'bg-green-100 text-green-800' };
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Produtos</h1>
            <p className="text-gray-600">
              Gerencie o catálogo de produtos da sua cantina
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link to="/dashboard/products/new">
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Cadastrar Produto
              </Button>
            </Link>
          </div>
        </div>

      {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Produtos</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-500">{stats.active} ativos</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Estoque Baixo</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.lowStock}</p>
                <p className="text-sm text-gray-500">produtos em falta</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Valor do Estoque</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatCurrency(stats.totalValue)}
                </p>
                <p className="text-sm text-gray-500">valor total</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Preço Médio</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatCurrency(stats.total > 0 ? products.reduce((sum, p) => sum + p.price, 0) / stats.total : 0)}
                </p>
                <p className="text-sm text-gray-500">por produto</p>
              </div>
            </div>
          </Card>
        </div>

      {/* Filters */}
        <Card className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 flex-1">
              <div className="flex-1 max-w-md">
                <Input
                  placeholder="Buscar produtos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  icon={Search}
                />
              </div>
              
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className={showFilters ? 'bg-blue-50 border-blue-300' : ''}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filtros
                </Button>
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoria
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Todas as categorias</option>
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Products List */}
        <Card>
          <div className="overflow-hidden">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                    Produto
                  </th>
                  <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20 hidden sm:table-cell">
                    Categoria
                  </th>
                  <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Preço
                  </th>
                  <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20 hidden lg:table-cell">
                    Estoque
                  </th>
                  <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24 hidden md:table-cell">
                    Status
                  </th>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 md:px-6 py-12 text-center">
                      <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">
                        {searchTerm || selectedCategory 
                          ? 'Nenhum produto encontrado com os filtros aplicados'
                          : 'Nenhum produto cadastrado ainda'
                        }
                      </p>
                      {!searchTerm && !selectedCategory && (
                        <Link to="/dashboard/products/new">
                          <Button className="mt-4 bg-blue-600 hover:bg-blue-700">
                            <Plus className="w-4 h-4 mr-2" />
                            Cadastrar Primeiro Produto
                          </Button>
                        </Link>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((product) => {
                    const stockStatus = getStockStatus(product);
                    return (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-3 md:px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 md:h-10 md:w-10">
                              <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <Package className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                              </div>
                            </div>
                            <div className="ml-3 md:ml-4 min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {product.name}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {product.description || 'Produto disponível'}
                              </div>
                              <div className="text-xs text-gray-500 truncate sm:hidden">
                                {product.category || 'Sem categoria'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 md:px-4 py-4 hidden sm:table-cell">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {product.category || 'Sem categoria'}
                          </span>
                        </td>
                        <td className="px-2 md:px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(product.price)}
                          </div>
                          {product.cost && (
                            <div className="text-xs text-gray-500">
                              Custo: {formatCurrency(product.cost)}
                            </div>
                          )}
                        </td>
                        <td className="px-2 md:px-4 py-4 hidden lg:table-cell">
                          <div className="text-sm text-gray-900">
                            {product.stock_quantity} {product.unit || 'un.'}
                          </div>
                          <div className="text-xs text-gray-500">
                            Mín: {product.min_stock}
                          </div>
                        </td>
                        <td className="px-2 md:px-4 py-4 hidden md:table-cell">
                          <div className="space-y-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}>
                              <span>{stockStatus.label}</span>
                            </span>
                            <div>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                product.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                <span>{product.active ? 'Ativo' : 'Inativo'}</span>
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-4 text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <button 
                              onClick={() => navigate(`/dashboard/products/${product.id}`)}
                              title="Ver detalhes"
                              className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-200"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => navigate(`/dashboard/products/${product.id}/edit`)}
                              title="Editar produto"
                              className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-amber-600 hover:bg-amber-50 hover:text-amber-700 transition-colors duration-200"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteProduct(product)}
                              title="Excluir produto"
                              className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-200"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
  );
};

export default ProductsPage;